import React, { useCallback, useState, useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import {
	getPartnerCompanyProject,
	getPartnerCompanyProjectEvents,
	getEventTypes,
	getPartnerCompany,
	getPrincipalGroupLabelsSettings,
	getFeatureFlags,
	getTags,
} from 'src/api/partner';

import { GenericDetailPage } from '@repo/components';
import { ProjectInfoBox } from '@repo/components';
import { ProjectForm } from '@repo/forms';
import { Spinner } from '@repo/components';
import SubcontractingTab from 'src/components/partner-portal/SubcontractingTab';
import { constructQuery } from '@repo/shared/helpers';
import { getVerifyPermission } from '@repo/shared/helpers';
import { PERMISSIONS } from '@repo/shared/domain';
import { Project } from '@repo/shared/domain';
import { EventEmitter } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/partner_/$partnerId/projects/$projectId')({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>) => ({
		search: (search.search as string) || '',
		page: search.page !== undefined ? Number(search.page) : 0,
		sort: (search.sort as string) || 'startTime:desc',
	}),
});

function RouteComponent() {
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const { partnerId, projectId } = Route.useParams();
	const { search, page, sort } = Route.useSearch();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const [isFormOpened, setIsFormOpened] = useState(false);

	// Get project data
	const {
		data: project,
		isLoading: projectLoading,
		error: projectError,
	} = useQuery({
		queryKey: ['partner-company-project', partnerId, projectId],
		queryFn: () => getPartnerCompanyProject(partnerId, projectId),
		enabled: !userLoading && !!authenticatedUser,
	});

	useBreadcrumbLevel(1, 'Projects', `/partner/${partnerId}/projects`);
	useBreadcrumbLevel(2, project?.name || 'Project Details');

	// Get partner company (subcontractingGroup)
	const { data: partnerCompany, isLoading: companyLoading } = useQuery({
		queryKey: ['partner-company', partnerId],
		queryFn: () => getPartnerCompany(partnerId),
		enabled: !userLoading && !!authenticatedUser,
	});

	// Get events for this project
	const { data: eventsResponse, isLoading: eventsLoading } = useQuery({
		queryKey: ['partner-company-project-events', partnerId, projectId, { search, page, sort }],
		queryFn: () =>
			getPartnerCompanyProjectEvents(partnerId, projectId, constructQuery({ search, page: page.toString(), sort })),
		enabled: !userLoading && !!authenticatedUser && !!project,
	});

	// Get event types
	const { data: eventTypesResponse, isLoading: eventTypesLoading } = useQuery({
		queryKey: ['event-types'],
		queryFn: () => getEventTypes(),
		enabled: !userLoading && !!authenticatedUser,
	});

	const eventTypes = eventTypesResponse?.data || [];

	// Get principal group labels settings
	const { data: principalGroupLabelsSettings, isLoading: principalGroupLabelsLoading } = useQuery({
		queryKey: ['principal-group-labels-settings'],
		queryFn: () => getPrincipalGroupLabelsSettings(),
		enabled: !userLoading && !!authenticatedUser,
	});

	// Get feature flags
	const { data: featureFlags } = useQuery({
		queryKey: ['feature-flags'],
		queryFn: () => getFeatureFlags(),
		enabled: !userLoading && !!authenticatedUser,
	});

	// Get tags
	const { data: tags } = useQuery({
		queryKey: ['partner-tags'],
		queryFn: () => getTags(),
		enabled: !userLoading && !!authenticatedUser,
	});

	const principalGroupLabels = useMemo(
		() => principalGroupLabelsSettings?.settings?.principalGroupLabels?.map((item) => item.value) || [],
		[principalGroupLabelsSettings],
	);

	const handleReload = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['partner-company-project-events', partnerId, projectId] });
	}, [queryClient, partnerId, projectId]);

	const handleAddEvent = async (data: unknown) => {
		try {
			const response = await fetch(`/api/partner-portal/groups/${partnerId}/projects/${projectId}/add-event`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(data),
			});
			if (!response.ok) throw new Error('Failed to create event');
			EventEmitter.emit('toast', { type: 'success', message: 'Event created successfully' });
			handleReload();
		} catch (error) {
			const errorMessage =
				(error as { message?: string; body?: { message?: string } })?.message ||
				(error as { message?: string; body?: { message?: string } })?.body?.message ||
				'Failed to create event';
			EventEmitter.emit('toast', { type: 'error', message: errorMessage });
			throw error; // Re-throw so the promise rejects
		}
	};

	const handleOpenForm = useCallback(() => {
		setIsFormOpened(true);
	}, []);

	const handleCloseForm = useCallback(() => {
		setIsFormOpened(false);
	}, []);

	const handleEditProject = useCallback(
		async (formData: Partial<Project> & { id?: string }) => {
			let isError = false;
			if (formData) {
				delete (formData as { projectData?: unknown }).projectData;
				try {
					const response = await fetch(`/api/partner-portal/groups/${partnerId}/projects/${projectId}`, {
						method: 'PUT',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify(formData),
					});

					if (!response.ok) throw new Error('Failed to save project');

					EventEmitter.emit('toast', {
						type: 'success',
						message: 'Project saved',
					});
				} catch (error: unknown) {
					isError = true;
					const errorMessage =
						(error as { message?: string; body?: { message?: string } })?.message ||
						(error as { message?: string; body?: { message?: string } })?.body?.message ||
						'Failed to save Project. Did you enter all data correctly?';
					EventEmitter.emit('toast', {
						type: 'error',
						message: errorMessage,
					});
				}
			}

			queryClient.invalidateQueries({ queryKey: ['partner-company-project', partnerId, projectId] });
			queryClient.invalidateQueries({ queryKey: ['partner-company-projects', partnerId] });

			if (!isError) {
				setIsFormOpened(false);
			}
		},
		[partnerId, projectId, queryClient],
	);

	// Determine if we should use simplified form (same logic as in ProjectForm)
	const shouldShowSimplifiedForm = useMemo(() => {
		if (!project) return true;
		if (principalGroupLabels.length > 1) return false;
		if (project.principalGroups && project.principalGroups.length > 0) {
			const existingLabels = [...new Set(project.principalGroups.map((pg: { label: string }) => pg.label))];
			if (existingLabels.length > 1) return false;
			if (existingLabels.length === 1 && existingLabels[0] !== principalGroupLabels[0]) return false;
		}
		return true;
	}, [project, principalGroupLabels]);

	// Pre-compute groupIds for the simplified form so it's available on first render
	const defaultProjectData = useMemo(() => {
		if (!project) return {};
		return {
			...project,
			...(shouldShowSimplifiedForm && project.principalGroups
				? { groupIds: project.principalGroups.map((pg: { groupId: string }) => pg.groupId) }
				: {}),
		};
	}, [project, shouldShowSimplifiedForm]);

	if (
		userLoading ||
		projectLoading ||
		companyLoading ||
		eventsLoading ||
		eventTypesLoading ||
		principalGroupLabelsLoading
	) {
		return (
			<div className="flex items-center justify-center h-full">
				<Spinner className="text-[#1E1E1E]" />
			</div>
		);
	}

	if (projectError || !project) {
		// Redirect back to projects list if project not found
		navigate({ to: `/partner/${partnerId}/projects` });
		return null;
	}

	return (
		<GenericDetailPage>
			<GenericDetailPage.Content>
				<ProjectInfoBox
					project={project}
					excludeZones={false}
					permissions={{
						canEdit: verifyPermission(PERMISSIONS.partnerCompanyProjectsEdit),
						canArchive: false,
					}}
					openEditForm={handleOpenForm}
				/>
			</GenericDetailPage.Content>
			<div className="p-6">
				<SubcontractingTab
					authenticatedUser={authenticatedUser}
					events={eventsResponse}
					addEvent={handleAddEvent}
					onReload={handleReload}
					types={eventTypes}
					permissions={{
						view: verifyPermission(PERMISSIONS.partnerCompanyEventsView),
						create: verifyPermission(PERMISSIONS.partnerCompanyEventsCreate),
						update: verifyPermission(PERMISSIONS.partnerCompanyEventsEdit),
						archive: false,
					}}
					project={project}
					projectId={projectId}
					groupId={partnerId}
					partnerId={partnerId}
					disableNavigation={false}
					isCompanyPortalEvent={true}
					subcontractingGroup={partnerCompany}
					tags={tags}
					usesTags={featureFlags?.USES_TAGS || false}
				/>
			</div>
			<ProjectForm
				isOpen={isFormOpened}
				onClose={handleCloseForm}
				onSubmit={handleEditProject}
				data={defaultProjectData}
				principalGroupLabels={principalGroupLabels}
				excludeZones={false}
				allowNewCompany={true}
			/>
		</GenericDetailPage>
	);
}
